import React, { useEffect } from 'react'
import { observer } from 'mobx-react'
import { ActionFlow, useActionFlow } from '~shared/lib/ActionFlow'
import { Alert, IconButton, Marker, SwitchToggle } from '@instacart/tds'
import { ArrowRightIcon, CloseIcon } from '@instacart/ids-core'
import { useParams } from 'react-router-dom'
import catalogStore from '../../Stores/CatalogStore'
import GlobalStore from '../../Stores/GlobalStore'
import { isActionAvailable } from '../../Utils/kdsActionPolicy'
import { KdsActionId } from '../../Types/KdsAuthorization'
import { useErrorHandling } from '~shared/hooks/useErrorHandling'
import styles from './ManageMenuModal.module.scss'
import { ItemOptionsSection } from './Components/ItemOptionsSection'
import { ManageMenuCloseFooter } from './Components/ManageMenuCloseFooter'

export const ManageMenuItemPage: React.FC<{
  path: string
  siteId: string
}> = observer(({ path, siteId }) => {
  const isToggling = catalogStore.togglingItemId !== null

  return (
    <ActionFlow.Page
      header={<ManageMenuItemHeader siteId={siteId} />}
      path={path}
      footer={<ManageMenuCloseFooter disabled={isToggling} />}
    >
      <ManageMenuItemPageBody siteId={siteId} />
    </ActionFlow.Page>
  )
})

const ManageMenuItemPageBody: React.FC<{
  siteId: string
}> = observer(({ siteId }) => {
  const { itemId } = useParams<{ itemId: string }>()
  const item = itemId ? catalogStore.getItemById(itemId) : undefined

  useEffect(() => {
    if (itemId) {
      void catalogStore.fetchItemOptionsForItem(siteId, itemId)
    }
  }, [siteId, itemId])

  if (!itemId || !item) {
    return (
      <div>
        <Alert variant="error">
          <span>Unable to load item details. Please go back and try again.</span>
        </Alert>
      </div>
    )
  }

  return (
    <div className={styles.itemDetail}>
      <ItemOptionsSection siteId={siteId} itemId={itemId} />
    </div>
  )
})

const ManageMenuItemHeader: React.FC<{
  siteId: string
}> = observer(({ siteId }) => {
  const { addError } = useErrorHandling()
  const { navigate, close } = useActionFlow()
  const { itemId } = useParams<{ itemId: string }>()
  const item = itemId ? catalogStore.getItemById(itemId) : undefined

  const isToggling = itemId ? catalogStore.isTogglingItem(itemId) : false
  const canToggle = Boolean(itemId && item && !isToggling)
  const canEditAvailability = isActionAvailable(
    GlobalStore.configFetch.data?.actionPolicy,
    KdsActionId.EditItemAvailability
  )

  const handleToggleAvailability = async (): Promise<void> => {
    if (!canToggle || !itemId || !item) return
    try {
      await catalogStore.setItemAvailability(siteId, itemId, !item.IsAvailableToday)
    } catch {
      addError('Failed to update item availability. Please try again.')
    }
  }

  return (
    <div className={styles.dialogHeader}>
      <div className={styles.dialogHeaderLeft}>
        <button
          type="button"
          className={styles.headerBackButton}
          onClick={() => navigate('/list')}
          disabled={isToggling}
          aria-label="Back"
        >
          <ArrowRightIcon className={styles.headerBackIcon} />
        </button>

        <h3>{item?.Name ?? 'Details'}</h3>
        <Marker
          text={item?.IsAvailableToday ? 'Available' : 'Unavailable'}
          variant={item?.IsAvailableToday ? 'success' : 'detrimental'}
        />
      </div>

      <div className={styles.dialogHeaderRight}>
        {canEditAvailability && (
          <div className={styles.headerToggle}>
            <div className={styles.headerToggleLabel}>In Stock</div>
            <SwitchToggle
              isSelected={Boolean(item?.IsAvailableToday)}
              onChange={() => void handleToggleAvailability()}
              disabled={!canToggle}
              label=""
              aria-label="Toggle item availability"
            />
          </div>
        )}

        <IconButton
          data-testid="manage-menu-close"
          onClick={close}
          variant="secondary"
          icon={CloseIcon}
        />
      </div>
    </div>
  )
})
